import { computed } from '@ember/object';
import { belongsTo, attr } from '@ember-data/model';
import ShModel from './sh-model';

/**
 * @type {Object}     Enums for 'type' field.
 */
const TYPES = {
  ADJUSTMENT: 'adjustment',
  ADVANCE: 'advance',
  ADVANCE_ADJUSTMENT: 'advance_adjustment',
  ADVANCE_FEE: 'advance_fee',
  BASE: 'base',
  BASE_FEE: 'base_fee',
  CANCELLATION: 'cancellation',
  EXPEDITE_FEE: 'expedite_fee',
  EXPENSE: 'expense',
  EXPENSE_FEE: 'expense_fee',
  SELECT_FEE: 'select_fee',
};

/**
 * NOTE - THESE ARE MAPPED TO THE PHP CONSTANTS IN ShBillableLineItem.php.  Any updates should go in both places.
 *
 * Types to calculate expedite fees on
 */
const EXPEDITE_FEE_TYPES = [
  TYPES.ADVANCE_ADJUSTMENT,
  TYPES.ADVANCE,
  TYPES.BASE,
  TYPES.CANCELLATION,
  TYPES.EXPENSE
];

/**
 * @type {string[]}   The valid fee types for a buyer
 */
const LINE_ITEM_FEE_TYPES = [
  TYPES.ADVANCE_FEE,
  TYPES.BASE_FEE,
  TYPES.EXPEDITE_FEE,
  TYPES.EXPENSE_FEE,
  TYPES.SELECT_FEE,
];

// These are the fees covered when a buyer elects to cover seller fees.
// Please keep these aligned with BUYER_SELLER_FEES_DICTIONARY in FeeStructure.php
const BUYER_COVER_SELLER_FEE_TYPES = [
  TYPES.BASE_FEE,
  TYPES.SELECT_FEE,
  TYPES.CANCELLATION,
];

/**
 * You *should* always use the description on the line item itself.
 * This is provided on the rare instance where you need a string to display in the client without a corresponding line item
 *
 * @type {Object}
 */
const TYPE_DESCRIPTIONS = {
  [TYPES.ADJUSTMENT]: 'Adjustment',
  [TYPES.ADVANCE]: 'Advance',
  [TYPES.ADVANCE_ADJUSTMENT]: 'Advance Adjustment',
  [TYPES.ADVANCE_FEE]: 'Advance Fee',
  [TYPES.BASE]: 'Base',
  [TYPES.BASE_FEE]: 'Service Fee',
  [TYPES.CANCELLATION]: 'Cancellation',
  [TYPES.EXPEDITE_FEE]: 'Expedite Fee',
  [TYPES.EXPENSE]: 'Expense',
  [TYPES.EXPENSE_FEE]: 'Expense Fee',
  [TYPES.SELECT_FEE]: 'Select Fee',
};

/**
 * @type {Object}     Enums for fake types which describe a piece of a combined field
 */
const BREAKDOWN_TYPES = {
  COVERED_CREATOR_FEE: 'covered_creator_fee',
};

/**
 * @type {Object}     Display name for each BREAKDOWN_TYPES enum
 */
const BREAKDOWN_TYPE_DESCRIPTIONS = {
  [BREAKDOWN_TYPES.COVERED_CREATOR_FEE]: "Creator's Fee",
};

/**
 * Class constants
 */
export const SH_BILLABLE_LINE_ITEM = {
  TYPES: TYPES,
  EXPEDITE_FEE_TYPES: EXPEDITE_FEE_TYPES,
  LINE_ITEM_FEE_TYPES: LINE_ITEM_FEE_TYPES,
  TYPE_DESCRIPTIONS: TYPE_DESCRIPTIONS,
  BUYER_COVER_SELLER_FEE_TYPES: BUYER_COVER_SELLER_FEE_TYPES,
  BREAKDOWN_TYPES: BREAKDOWN_TYPES,
  BREAKDOWN_TYPE_DESCRIPTIONS: BREAKDOWN_TYPE_DESCRIPTIONS,
};

const ShBillableLineItem = ShModel.extend({
  amount: attr('number'),
  createdAt: attr('date'),
  description: attr('string'),
  dueAt: attr('date'),
  type: attr('string'),
  updatedAt: attr('date'),

  application: belongsTo('application', { async: false }),

  /**
   * @type {Boolean}    True if this line item 'type' is a expedited fee
   */
  isExpediteFee: computed('type', function() {
    return this.get('type') === SH_BILLABLE_LINE_ITEM.TYPES.EXPEDITE_FEE;
  }),

  /**
   * Function to get a description suitable for admins
   */
  adminDescription: undefined
});

export default ShBillableLineItem;
